import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, functions, storage } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { theme } from '../styles/MUITheme';
import styled from '@emotion/styled';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Tooltip,
  TextField,
  Alert
} from '@mui/material';
import Modal from '@mui/material/Modal';
import { startOfDay } from 'date-fns';
import { LINE_STATUSES } from '../firebase/schema';
import { lineStatusAfterCut } from '../utils/lineCutStatus';
import { getMockupUrl } from '../utils/fileHelpers';
import { formatDate } from '../utils/dateFormatter';
import { useFacility } from '../context/FacilityContext';
import { filterByFacility } from '../utils/facilities';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// ─── Styled Components ────────────────────────────────────────────────────────

const MetricBox = styled(Box)`
  background-color: var(--al-surface);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-width: 160px;

  .metric-value {
    font-size: 1.8em;
    font-weight: bold;
    color: var(--al-primary);
    margin-bottom: 8px;
  }

  .metric-label {
    font-size: 0.9em;
    color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &.danger .metric-value {
    color: #FF4C4C;
  }
`;

const CustomCheckboxContainer = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(var(--al-primary-rgb), 0.5);
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  margin: 0 auto;

  ${props => props.checked && `
    background-color: var(--al-primary);
    border-color: var(--al-primary);
  `}

  ${props => props.disabled && `
    opacity: 0.5;
    cursor: not-allowed;
  `}

  &:hover {
    border-color: ${props => !props.disabled && 'var(--al-primary)'};
  }

  svg {
    width: 14px;
    height: 14px;
    fill: #222;
    opacity: ${props => props.checked ? 1 : 0};
  }
`;

// ─── Drive File Matching Utilities ────────────────────────────────────────────

const normalizeForMatch = (str) =>
  str
    .toLowerCase()
    .replace(/x\s*\d+\.[a-z]+$/i, '') // strip x15.png or x 15.png suffix
    .replace(/\.[a-z]+$/i, '')         // strip extension
    .replace(/[_\-]+/g, ' ')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const TOKEN_ALIASES = {
  uni: ['university'],
  univ: ['university'],
  tshirt: ['tee', 'shirt'],
  tshirts: ['tee', 'shirt'],
  tee: ['shirt'],
  crewneck: ['sweatshirt'],
  hoodie: ['sweatshirt'],
  isu: ['iowa', 'state']
};

const TOKEN_STOP_WORDS = new Set([
  'of', 'the', 'and', 'for', 'with',
  'shirt', 'shirts', 'tee', 'tees',
  'crewneck', 'sweatshirt', 'hoodie',
  'adult', 'youth', 'unisex'
]);

const tokenizeForMatch = (str) => {
  const base = normalizeForMatch(str).split(/\s+/).filter(Boolean);
  const out = new Set();
  for (const raw of base) {
    const token = raw.endsWith('s') && raw.length > 3 ? raw.slice(0, -1) : raw;
    if (token && !TOKEN_STOP_WORDS.has(token)) out.add(token);
    const aliases = TOKEN_ALIASES[token] || [];
    for (const alias of aliases) {
      if (alias && !TOKEN_STOP_WORDS.has(alias)) out.add(alias);
    }
  }
  return out;
};

const tokenOverlapScore = (lineLabel, fileLabel) => {
  const lineTokens = tokenizeForMatch(lineLabel);
  const fileTokens = tokenizeForMatch(fileLabel);
  if (lineTokens.size === 0 || fileTokens.size === 0) return 0;

  let overlap = 0;
  for (const t of fileTokens) {
    if (lineTokens.has(t)) overlap++;
  }

  const jaccard = overlap / new Set([...lineTokens, ...fileTokens]).size;
  const fileCoverage = overlap / fileTokens.size; // key: short filenames should still match if fully covered
  const lineCoverage = overlap / lineTokens.size;

  return Math.max(jaccard, fileCoverage * 0.92, lineCoverage * 0.75);
};

const findMatchingFile = (line, files) => {
  const name = line.designName || '';
  const client = line.customerName || '';
  const searchLabel = `${name} ${client}`.trim();
  let best = null;
  let bestScore = 0;
  for (const file of files) {
    const score = tokenOverlapScore(searchLabel, file.name || '');
    if (score > bestScore) {
      bestScore = score;
      best = file;
    }
  }
  return bestScore >= 0.46 ? best : null;
};

const getRequiredLocations = (line) => {
  const { front, back, other } = getFilmsPerLocation(line);
  const required = [];
  if (front > 0) required.push('front');
  if (back > 0) required.push('back');
  if (other > 0) required.push('other');
  return required;
};

const getLineMappedFiles = (line, files) => {
  const mapped = Array.isArray(line?.driveFileMappings) ? line.driveFileMappings : [];
  if (mapped.length > 0) {
    return mapped.map((m) => {
      const exact = files.find((f) => f.id === m.fileId);
      if (exact) return { ...exact, location: m.location || 'front' };
      return {
        id: m.fileId,
        name: m.fileName || 'Mapped file',
        webViewLink: m.webViewLink || '#',
        thumbnailLink: m.thumbnailLink || null,
        location: m.location || 'front'
      };
    });
  }
  // Legacy single-file mapping fallback
  if (!line?.driveFileId) return [];
  const exact = files.find((f) => f.id === line.driveFileId);
  if (exact) return [{ ...exact, location: 'front' }];
  if (!line.driveFileName && !line.driveWebViewLink) return [];
  return [{
    id: line.driveFileId,
    name: line.driveFileName || 'Mapped file',
    webViewLink: line.driveWebViewLink || '#',
    thumbnailLink: line.driveThumbnailLink || null,
    location: 'front'
  }];
};

const buildLineFileStatus = (line, files) => {
  const requiredLocations = getRequiredLocations(line);
  const mappedFiles = getLineMappedFiles(line, files);
  const autoFile = mappedFiles.length === 0 ? findMatchingFile(line, files) : null;
  const assignedFiles = mappedFiles.length > 0 ? mappedFiles : (autoFile ? [autoFile] : []);
  const requiredCount = Math.max(requiredLocations.length, 1);
  const assignedCount = assignedFiles.length;

  if (assignedCount === 0) {
    return {
      tone: 'miss',
      tooltip: 'No matching file found in hot folder',
      primaryFile: null,
      assignedCount,
      requiredCount
    };
  }

  const lineQty = Number(line.totalQuantity) || 0;
  const qtyShort = assignedFiles.some((f) => {
    const fileQty = extractFileQuantity(f.name || '');
    return fileQty !== null && fileQty < lineQty;
  });

  if (assignedCount < requiredCount) {
    return {
      tone: 'warn',
      tooltip: `Only ${assignedCount}/${requiredCount} required artwork files assigned`,
      primaryFile: assignedFiles[0] || null,
      assignedCount,
      requiredCount
    };
  }

  if (qtyShort) {
    return {
      tone: 'warn',
      tooltip: 'Artwork found, but at least one filename quantity is below order quantity',
      primaryFile: assignedFiles[0] || null,
      assignedCount,
      requiredCount
    };
  }

  return {
    tone: 'ok',
    tooltip: `Artwork coverage complete (${assignedCount}/${requiredCount})`,
    primaryFile: assignedFiles[0] || null,
    assignedCount,
    requiredCount
  };
};

const getDriveThumbnailSrc = (functionsInstance, file) => {
  if (!file?.id) return null;
  const projectId = functionsInstance?.app?.options?.projectId || 'apparel-logistics-447003-e318e';
  const region = 'us-central1';
  const params = new URLSearchParams({ fileId: file.id });
  if (file.resourceKey) params.set('resourceKey', file.resourceKey);
  return `https://${region}-${projectId}.cloudfunctions.net/getDriveThumbnail?${params.toString()}`;
};

const inferLocationFromFileName = (fileName, requiredLocations = []) => {
  const name = normalizeForMatch(fileName || '');
  if (!name) return null;

  const required = new Set((requiredLocations || []).map((l) => String(l).toLowerCase()));
  const contains = (re) => re.test(name);
  const canUse = (loc) => required.size === 0 || required.has(loc);

  if (canUse('front') && (contains(/\bfront\b/) || contains(/\bfrt\b/) || contains(/\bf\b/))) return 'front';
  if (canUse('back') && (contains(/\bback\b/) || contains(/\bbk\b/) || contains(/\bb\b/))) return 'back';
  if (canUse('other') && (contains(/\bother\b/) || contains(/\bos\b/) || contains(/\bsleeve\b/) || contains(/\bleft chest\b/) || contains(/\blc\b/))) return 'other';
  return null;
};

const extractFileQuantity = (fileName) => {
  const match = fileName.match(/x(\d+)\.[a-z]+$/i);
  return match ? parseInt(match[1], 10) : null;
};

// ─── Film Count Utilities (unchanged logic) ───────────────────────────────────

const getFilmsPerLocation = (line) => {
  const locs = line.printLocations || [];
  const hasLoc = (key) => locs.some(l => String(l).toLowerCase() === key);
  let f, b, o;
  if (locs.length > 0) {
    f = hasLoc('front') ? 1 : 0;
    b = hasLoc('back') ? 1 : 0;
    o = hasLoc('other') ? 1 : 0;
  } else {
    f = (Array.isArray(line.frontInkColors) && line.frontInkColors.length > 0) ? 1 : 0;
    b = (Array.isArray(line.backInkColors) && line.backInkColors.length > 0) ? 1 : 0;
    o = (Array.isArray(line.otherInkColors) && line.otherInkColors.length > 0) ? 1 : 0;
  }
  return { front: f, back: b, other: o, total: f + b + o };
};

const getFilmsDisplay = (line) => {
  const qty = Number(line.totalQuantity) || 0;
  const { front: f, back: b, other: o } = getFilmsPerLocation(line);
  return {
    front: f ? qty : 0,
    back: b ? qty : 0,
    other: o ? qty : 0,
    total: (f ? qty : 0) + (b ? qty : 0) + (o ? qty : 0)
  };
};

const getFilmCreationMinutes = (line) => getFilmsDisplay(line).total * 2;

const isOverdue = (job, today) => {
  if (!job || !today || !job.dueDate) return false;
  return startOfDay(new Date(job.dueDate)) < today;
};

// ─── File Status Indicator ────────────────────────────────────────────────────

const statusDotStyle = (tone) => {
  const palettes = {
    ok: {
      bg: '#7CFFB2',
      ring: 'rgba(124,255,178,0.45)',
      glow: 'rgba(124,255,178,0.55)'
    },
    warn: {
      bg: '#FFC46B',
      ring: 'rgba(255,196,107,0.45)',
      glow: 'rgba(255,196,107,0.5)'
    },
    miss: {
      bg: '#FF7A86',
      ring: 'rgba(255,122,134,0.38)',
      glow: 'rgba(255,122,134,0.45)'
    }
  };
  const c = palettes[tone] || palettes.miss;
  return {
    width: 11,
    height: 11,
    borderRadius: '50%',
    background: c.bg,
    border: `1px solid ${c.ring}`,
    boxShadow: `0 0 0 3px ${c.ring}, 0 0 14px ${c.glow}`,
    transition: 'transform 120ms ease'
  };
};

const StatusDot = ({ tone }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...statusDotStyle(tone)
    }}
  />
);

const FileStatusDot = ({ status }) => {
  const hasLink = Boolean(status?.primaryFile?.webViewLink && status.primaryFile.webViewLink !== '#');
  return (
    <Tooltip title={status?.tooltip || ''}>
      {hasLink ? (
        <a href={status.primaryFile.webViewLink} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
          <StatusDot tone={status?.tone || 'miss'} />
        </a>
      ) : (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <StatusDot tone={status?.tone || 'miss'} />
        </span>
      )}
    </Tooltip>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const FilmList = () => {
  const { facilityId } = useFacility();
  const [filmList, setFilmList] = useState([]);
  const [driveFiles, setDriveFiles] = useState([]);
  const [driveLoading, setDriveLoading] = useState(true);
  /** Set when listHotFolderFiles fails (was only console.warn before). */
  const [driveListError, setDriveListError] = useState(null);
  const [driveTestLoading, setDriveTestLoading] = useState(false);
  /** Last driveHotFolderHealthCheck result: { ok, data } | { ok: false, message, code, details } */
  const [driveTestResult, setDriveTestResult] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [artModalOpen, setArtModalOpen] = useState(false);
  const [artModalUrl, setArtModalUrl] = useState(null);
  const [cutDialogOpen, setCutDialogOpen] = useState(false);
  const [lineToMark, setLineToMark] = useState(null);
  const [isMarking, setIsMarking] = useState(false);
  const [mapperOpen, setMapperOpen] = useState(false);
  const [mapperLine, setMapperLine] = useState(null);
  const [mapperQuery, setMapperQuery] = useState('');
  const [mapperSavingFileId, setMapperSavingFileId] = useState(null);

  const filmToday = startOfDay(new Date());

  const fetchFilmList = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const q = query(
        collection(db, 'orderLines'),
        where('type', 'in', ['dtf', 'DTF'])
      );
      const querySnapshot = await getDocs(q);
      const lines = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      const orderIds = [...new Set(lines.map(l => l.orderId).filter(Boolean))];
      const orderDocs = await Promise.all(
        orderIds.map(async orderId => {
          const orderSnap = await getDoc(doc(db, 'orders', orderId));
          return { id: orderId, ...orderSnap.data() };
        })
      );
      const orderMap = new Map(orderDocs.map(o => [o.id, o]));

      const active = filterByFacility(
        lines.map(line => {
          const order = orderMap.get(line.orderId);
          return {
            ...line,
            facilityId: line.facilityId || order?.facilityId,
            orderNumber: order?.poNumber || 'N/A',
            customerName: order?.customerName || 'N/A',
            dueDate: line.dueDate ?? order?.dueDate ?? null,
            _archived: order?.archived === true
          };
        }),
        facilityId
      ).filter(line => !line.isCut && !line._archived);

      setFilmList(active);
    } catch (err) {
      console.error('Error fetching film list:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDriveFiles = async () => {
    setDriveLoading(true);
    setDriveListError(null);
    try {
      const listFiles = httpsCallable(functions, 'listHotFolderFiles');
      const result = await listFiles();
      setDriveFiles(result.data.files || []);
    } catch (err) {
      console.warn('Could not load Drive files:', err.message);
      setDriveFiles([]);
      setDriveListError(err.message || String(err));
    } finally {
      setDriveLoading(false);
    }
  };

  const testDriveConnection = async () => {
    setDriveTestLoading(true);
    setDriveTestResult(null);
    try {
      const health = httpsCallable(functions, 'driveHotFolderHealthCheck');
      const result = await health({});
      setDriveTestResult({ ok: true, data: result.data });
    } catch (err) {
      setDriveTestResult({
        ok: false,
        message: err.message || String(err),
        code: err.code,
        details: err.details
      });
    } finally {
      setDriveTestLoading(false);
    }
  };

  useEffect(() => {
    fetchFilmList();
    fetchDriveFiles();
  }, [facilityId]);

  // Build a map of line id → file status info
  const fileStatuses = useMemo(() => {
    const map = {};
    for (const line of filmList) {
      map[line.id] = buildLineFileStatus(line, driveFiles);
    }
    return map;
  }, [filmList, driveFiles]);

  const mapperFiles = useMemo(() => {
    const q = normalizeForMatch(mapperQuery || '');
    if (!q) return driveFiles;
    return driveFiles.filter((file) => normalizeForMatch(file.name || '').includes(q));
  }, [driveFiles, mapperQuery]);

  const handleCutClick = (line) => {
    setLineToMark(line);
    setCutDialogOpen(true);
  };

  const handleCutCancel = () => {
    setCutDialogOpen(false);
    setLineToMark(null);
  };

  const handleCutConfirm = async () => {
    if (!lineToMark) return;
    setIsMarking(true);
    try {
      const lineRef = doc(db, 'orderLines', lineToMark.id);

      const newStatus = lineStatusAfterCut(lineToMark);
      await updateDoc(lineRef, { isCut: true, status: newStatus });
      setFilmList(prev => prev.filter(l => l.id !== lineToMark.id));
      setCutDialogOpen(false);
      setLineToMark(null);
    } catch (err) {
      console.error('Error updating line:', err);
      alert('Error updating status: ' + err.message);
    } finally {
      setIsMarking(false);
    }
  };

  const handleArtModalOpen = (job) => {
    setArtModalUrl(getMockupUrl(job));
    setArtModalOpen(true);
  };
  const handleArtModalClose = () => setArtModalOpen(false);

  const handleRowOpen = (job) => {
    // Single unified flow: always open mapper so users can assign/reassign quickly.
    handleOpenMapper(job);
  };

  const handleOpenMapper = (line) => {
    setMapperLine(line);
    setMapperQuery('');
    setMapperOpen(true);
  };

  const handleCloseMapper = () => {
    setMapperOpen(false);
    setMapperLine(null);
    setMapperQuery('');
    setMapperSavingFileId(null);
  };

  const handleMapArtwork = async (location, file) => {
    if (!mapperLine || !file?.id) return;
    setMapperSavingFileId(`${file.id}:${location}`);
    try {
      const required = getRequiredLocations(mapperLine);
      const inferredLocation = inferLocationFromFileName(file.name, required);
      const targetLocation = location || inferredLocation || 'front';
      const existingMappings = Array.isArray(mapperLine.driveFileMappings) ? mapperLine.driveFileMappings : [];
      const nextMapping = {
        location: targetLocation,
        fileId: file.id,
        fileName: file.name || null,
        webViewLink: file.webViewLink || null,
        thumbnailLink: file.thumbnailLink || null,
        mappedAt: new Date().toISOString()
      };
      const mergedMappings = [
        ...existingMappings.filter((m) => m.location !== targetLocation),
        nextMapping
      ];

      // Also persist assigned Drive file into Firebase Storage so FilesModal/line files have AL-native production assets.
      const copyDriveToStorage = httpsCallable(functions, 'copyDriveToStorage');
      const driveToStorageResult = await copyDriveToStorage({
        fileId: file.id,
        fileName: file.name || `${targetLocation}-production.png`,
        lineId: mapperLine.id,
        location: targetLocation,
        resourceKey: file.resourceKey || null
      });
      const storagePath = driveToStorageResult?.data?.storagePath;
      let storageUrl = null;
      if (storagePath) {
        storageUrl = await getDownloadURL(storageRef(storage, storagePath));
      }

      const existingProduction = Array.isArray(mapperLine?.files?.production)
        ? mapperLine.files.production
        : [];
      const nextProductionEntry = {
        name: file.name || `${targetLocation}-production`,
        url: storageUrl,
        type: `${targetLocation}-production`,
        storagePath: storagePath || null,
        source: 'drive',
        driveFileId: file.id
      };
      const mergedProduction = [
        ...existingProduction.filter((p) => p?.type !== `${targetLocation}-production`),
        nextProductionEntry
      ];

      const lineRef = doc(db, 'orderLines', mapperLine.id);
      await updateDoc(lineRef, {
        driveFileMappings: mergedMappings,
        'files.production': mergedProduction,
        // Back-compat fields retained for existing flows
        driveFileId: file.id,
        driveFileName: file.name || null,
        driveWebViewLink: file.webViewLink || null,
        driveThumbnailLink: file.thumbnailLink || null,
        driveMappedAt: new Date().toISOString()
      });
      setFilmList((prev) =>
        prev.map((line) =>
          line.id === mapperLine.id
            ? {
              ...line,
              driveFileMappings: mergedMappings,
              files: {
                ...(line.files || {}),
                production: mergedProduction
              },
              driveFileId: file.id,
              driveFileName: file.name || null,
              driveWebViewLink: file.webViewLink || null,
              driveThumbnailLink: file.thumbnailLink || null,
              driveMappedAt: new Date().toISOString()
            }
            : line
        )
      );
      setMapperLine((prev) => (prev ? {
        ...prev,
        driveFileMappings: mergedMappings,
        files: {
          ...(prev.files || {}),
          production: mergedProduction
        },
        driveFileId: file.id,
        driveFileName: file.name || null,
        driveWebViewLink: file.webViewLink || null,
        driveThumbnailLink: file.thumbnailLink || null,
        driveMappedAt: new Date().toISOString()
      } : prev));
      const filled = mergedMappings.map((m) => m.location);
      const nextUnfilled = required.find((loc) => !filled.includes(loc));
      if (!nextUnfilled) {
        handleCloseMapper();
      }
    } catch (err) {
      alert(`Could not map artwork: ${err.message}`);
    } finally {
      setMapperSavingFileId(null);
    }
  };

  const handleUnassignArtwork = async (location) => {
    if (!mapperLine || !location) return;
    setMapperSavingFileId(`unassign:${location}`);
    try {
      const existingMappings = Array.isArray(mapperLine.driveFileMappings) ? mapperLine.driveFileMappings : [];
      const remainingMappings = existingMappings.filter((m) => m.location !== location);

      const lineRef = doc(db, 'orderLines', mapperLine.id);
      await updateDoc(lineRef, {
        driveFileMappings: remainingMappings,
        driveMappedAt: new Date().toISOString()
      });

      setFilmList((prev) =>
        prev.map((line) =>
          line.id === mapperLine.id
            ? {
              ...line,
              driveFileMappings: remainingMappings,
              driveMappedAt: new Date().toISOString()
            }
            : line
        )
      );

      setMapperLine((prev) => (prev ? {
        ...prev,
        driveFileMappings: remainingMappings,
        driveMappedAt: new Date().toISOString()
      } : prev));
    } catch (err) {
      alert(`Could not unassign artwork: ${err.message}`);
    } finally {
      setMapperSavingFileId(null);
    }
  };

  const metrics = useMemo(() => {
    const totalFilms = filmList.reduce((sum, l) => sum + getFilmsDisplay(l).total, 0);
    const overdueCount = filmList
      .filter(j => isOverdue(j, filmToday))
      .reduce((sum, l) => sum + getFilmsDisplay(l).total, 0);
    const matchedCount = filmList.filter(l => (fileStatuses[l.id]?.tone || 'miss') !== 'miss').length;
    return { totalFilms, overdueCount, matchedCount };
  }, [filmList, fileStatuses, filmToday]);

  if (isLoading) {
    return (
      <MUIThemeProvider theme={theme}>
        <Box sx={{ p: 3, mt: 8, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress sx={{ color: 'var(--al-primary)' }} />
          <Typography>Loading film list...</Typography>
        </Box>
      </MUIThemeProvider>
    );
  }

  if (error) {
    return (
      <MUIThemeProvider theme={theme}>
        <Box sx={{ p: 3, mt: 8 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </MUIThemeProvider>
    );
  }

  return (
    <MUIThemeProvider theme={theme}>
      <Box sx={{ width: '100%', padding: '80px 20px 20px 20px', backgroundColor: 'var(--al-bg)', minHeight: '100vh' }}>

        {/* Metrics */}
        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <MetricBox>
            <div className="metric-value">{metrics.totalFilms}</div>
            <div className="metric-label">Total Films to Cut</div>
          </MetricBox>
          <MetricBox className={metrics.overdueCount > 0 ? 'danger' : ''}>
            <div className="metric-value">{metrics.overdueCount}</div>
            <div className="metric-label">Overdue Films</div>
          </MetricBox>
          <MetricBox>
            <div className="metric-value">
              {driveLoading ? '…' : `${metrics.matchedCount} / ${filmList.length}`}
            </div>
            <div className="metric-label">Files Found</div>
            {!driveLoading && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'rgba(255,255,255,0.45)', textTransform: 'none', letterSpacing: '0.02em' }}>
                {driveListError
                  ? 'Drive list failed — use Test connection'
                  : `${driveFiles.length} file(s) in hot folder`}
              </Typography>
            )}
          </MetricBox>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'center', minWidth: 200 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={driveLoading}
              onClick={() => fetchDriveFiles()}
              sx={{ color: 'var(--al-primary)', borderColor: 'rgba(var(--al-primary-rgb), 0.5)' }}
            >
              Refresh Drive list
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={driveTestLoading}
              onClick={() => testDriveConnection()}
              sx={{ color: 'var(--al-primary)', borderColor: 'rgba(var(--al-primary-rgb), 0.5)' }}
            >
              {driveTestLoading ? 'Testing…' : 'Test Drive connection'}
            </Button>
          </Box>
        </Box>

        {driveListError ? (
          <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(211,47,47,0.12)', color: '#ffcdd2' }}>
            Could not load hot folder files: {driveListError}
          </Alert>
        ) : null}

        {driveTestResult ? (
          <Alert
            severity={driveTestResult.ok ? 'success' : 'error'}
            onClose={() => setDriveTestResult(null)}
            sx={{ mb: 2, ...(driveTestResult.ok ? { bgcolor: 'rgba(var(--al-primary-rgb), 0.08)', color: '#e8f5e9' } : {}) }}
          >
            {driveTestResult.ok ? (
              <>
                Drive API reachable. Service account:{' '}
                <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace', color: 'inherit' }}>
                  {driveTestResult.data?.serviceAccountEmail}
                </Typography>
                . Sample list returned{' '}
                {driveTestResult.data?.listedFileCount ?? driveTestResult.data?.visibleFileCountSample ?? '—'} file(s) in folder
                {driveTestResult.data?.morePagesRemain ? ' (first 1000; more pages exist)' : ''}. Folder ID{' '}
                <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {driveTestResult.data?.folderId}
                </Typography>
                .
              </>
            ) : (
              <>
                Health check failed: {driveTestResult.message}
                {driveTestResult.code ? ` (${driveTestResult.code})` : ''}
                {driveTestResult.details?.hint ? (
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                    {driveTestResult.details.hint}
                  </Typography>
                ) : null}
              </>
            )}
          </Alert>
        ) : null}

        {/* Film Table */}
        {filmList.length === 0 ? (
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            🎉 No films left to cut!
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ backgroundColor: 'var(--al-surface)', boxShadow: 'none', border: '1px solid rgba(255,255,255,0.1)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 48 }} />
                  <TableCell align="center" sx={{ width: 40 }}>File</TableCell>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Design Name</TableCell>
                  <TableCell align="center">Films (F/B/O)</TableCell>
                  <TableCell align="center">Total</TableCell>
                  <TableCell>Due Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filmList
                  .slice()
                  .sort((a, b) => {
                    const dA = a.dueDate ? new Date(a.dueDate) : new Date(9999, 11, 31);
                    const dB = b.dueDate ? new Date(b.dueDate) : new Date(9999, 11, 31);
                    return dA - dB;
                  })
                  .map((job) => {
                    const { front, back, other, total } = getFilmsDisplay(job);
                    const overdue = isOverdue(job, filmToday);
                    return (
                      <TableRow
                        key={job.id}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'rgba(var(--al-primary-rgb), 0.05)' },
                          ...(overdue && { '& td': { color: '#FF4C4C', fontWeight: 700 } })
                        }}
                        onClick={() => handleRowOpen(job)}
                      >
                        <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                          <CustomCheckboxContainer
                            checked={job.isCut}
                            disabled={job.isCut}
                            onClick={() => !job.isCut && handleCutClick(job)}
                          >
                            <svg viewBox="0 0 24 24">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                            </svg>
                          </CustomCheckboxContainer>
                        </TableCell>
                        <TableCell align="center" onClick={e => e.stopPropagation()}>
                          {driveLoading
                            ? <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>…</span>
                            : (
                              <Box
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                onClick={() => handleOpenMapper(job)}
                              >
                                <FileStatusDot status={fileStatuses[job.id]} />
                              </Box>
                            )
                          }
                        </TableCell>
                        <TableCell>{job.orderNumber}</TableCell>
                        <TableCell>{job.customerName}</TableCell>
                        <TableCell>{job.designName || 'Unnamed Design'}</TableCell>
                        <TableCell align="center">{front} / {back} / {other}</TableCell>
                        <TableCell align="center">{total}</TableCell>
                        <TableCell>{formatDate(job.dueDate)}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Cut Confirmation Dialog */}
        <Dialog open={cutDialogOpen} onClose={handleCutCancel}>
          <DialogTitle>Mark Film as Cut?</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 1 }}>Are you sure you want to mark this film as cut?</Typography>
            {lineToMark && (
              <Box sx={{ background: 'rgba(var(--al-primary-rgb), 0.1)', p: 2, borderRadius: 1, mt: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>{lineToMark.designName || 'Unnamed Design'}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {lineToMark.customerName} • Invoice #{lineToMark.orderNumber}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {(() => {
                    const { front, back, other, total } = getFilmsDisplay(lineToMark);
                    return `Films: ${front} / ${back} / ${other} (total ${total})`;
                  })()}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCutCancel} disabled={isMarking}>No</Button>
            <Button onClick={handleCutConfirm} color="primary" variant="contained" disabled={isMarking}>
              {isMarking ? 'Marking...' : 'Yes, Mark as Cut'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={mapperOpen} onClose={handleCloseMapper} maxWidth="lg" fullWidth>
          <DialogTitle>Map Artwork to Line Item</DialogTitle>
          <DialogContent dividers>
            {mapperLine && (
              <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                {mapperLine.customerName} • Invoice #{mapperLine.orderNumber} • {mapperLine.designName || 'Unnamed Design'}
              </Typography>
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1.2fr' },
                gap: 2
              }}
            >
              <Box
                sx={{
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 1,
                  p: 1.5,
                  background: 'rgba(255,255,255,0.02)'
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Current Mockup</Typography>
                {mapperLine && getMockupUrl(mapperLine) ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <img
                      src={getMockupUrl(mapperLine)}
                      alt={`${mapperLine.designName || 'Design'} mockup`}
                      style={{ maxWidth: '100%', maxHeight: '420px', borderRadius: 8, background: '#222' }}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No mockup image attached to this line item yet.
                  </Typography>
                )}
              </Box>

              <Box>
                {mapperLine && (
                  <Box sx={{ mb: 1.5, p: 1.25, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1 }}>
                    {(() => {
                      const required = getRequiredLocations(mapperLine);
                      const locationOptions = required.length > 0 ? required : ['front'];
                      const mappings = Array.isArray(mapperLine.driveFileMappings) ? mapperLine.driveFileMappings : [];
                      const byLoc = Object.fromEntries(mappings.map((m) => [m.location, m]));
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
                            Locations:
                          </Typography>
                          {locationOptions.map((loc) => {
                            const assigned = Boolean(byLoc[loc]?.fileId);
                            return (
                              <Box
                                key={loc}
                                sx={{
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  border: assigned ? '1px solid rgba(124,255,178,0.6)' : '1px solid rgba(255,122,134,0.4)',
                                  background: assigned ? 'rgba(124,255,178,0.12)' : 'rgba(255,122,134,0.08)',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.75
                                }}
                              >
                                <span style={{ fontSize: 11 }}>{assigned ? '✓' : '○'}</span>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                  {loc.toUpperCase()}
                                </Typography>
                                {assigned && (
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {byLoc[loc]?.fileName || 'Assigned'}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })()}
                  </Box>
                )}
                <TextField
                  fullWidth
                  size="small"
                  label="Search Drive files"
                  value={mapperQuery}
                  onChange={(e) => setMapperQuery(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ maxHeight: 420, overflowY: 'auto', display: 'grid', gap: 1.2 }}>
                  {mapperFiles.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      No files match this search.
                    </Typography>
                  )}
                  {mapperFiles.map((file) => (
                    <Box
                      key={file.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1,
                        borderRadius: 1,
                        border: '1px solid rgba(255,255,255,0.12)'
                      }}
                    >
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: 1,
                          overflow: 'hidden',
                          background: 'rgba(255,255,255,0.08)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        {getDriveThumbnailSrc(functions, file) ? (
                          <>
                            <img
                              src={getDriveThumbnailSrc(functions, file)}
                              alt={file.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={(e) => {
                                const img = e.currentTarget;
                                if (!img.dataset.fallbackDone && file.thumbnailLink) {
                                  img.dataset.fallbackDone = '1';
                                  img.src = file.thumbnailLink;
                                  return;
                                }
                                img.style.display = 'none';
                                const fallback = img.nextElementSibling;
                                if (fallback) fallback.style.display = 'block';
                              }}
                            />
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'none' }}>No preview</Typography>
                          </>
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>No preview</Typography>
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.name}
                        </Typography>
                      </Box>
                      {(() => {
                        const required = mapperLine ? getRequiredLocations(mapperLine) : ['front'];
                        const locationOptions = required.length > 0 ? required : ['front'];
                        const mappings = Array.isArray(mapperLine?.driveFileMappings) ? mapperLine.driveFileMappings : [];
                        const byLoc = Object.fromEntries(mappings.map((m) => [m.location, m]));
                        return (
                          <Box sx={{ display: 'flex', gap: 0.7, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {locationOptions.map((loc) => {
                              const current = byLoc[loc];
                              const isSame = current?.fileId === file.id;
                              const busyKey = `${file.id}:${loc}`;
                              return (
                                <Button
                                  key={`${file.id}-${loc}`}
                                  size="small"
                                  variant={isSame ? 'contained' : 'contained'}
                                  color={isSame ? 'error' : 'primary'}
                                  onClick={() => (isSame ? handleUnassignArtwork(loc) : handleMapArtwork(loc, file))}
                                  disabled={mapperSavingFileId === busyKey || mapperSavingFileId === `unassign:${loc}`}
                                  sx={{ minWidth: 104 }}
                                >
                                  {(mapperSavingFileId === busyKey || mapperSavingFileId === `unassign:${loc}`)
                                    ? 'Saving...'
                                    : isSame
                                      ? `Unassign ${loc[0].toUpperCase() + loc.slice(1)}`
                                      : `${current ? 'Reassign' : 'Assign'} ${loc[0].toUpperCase() + loc.slice(1)}`}
                                </Button>
                              );
                            })}
                          </Box>
                        );
                      })()}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseMapper}>Close</Button>
          </DialogActions>
        </Dialog>

      </Box>

      {/* Art Preview Modal */}
      {artModalOpen && artModalUrl && (
        <Modal open={artModalOpen} onClose={handleArtModalClose}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', outline: 'none', position: 'relative' }}>
            <button
              onClick={handleArtModalClose}
              style={{ position: 'absolute', top: 24, right: 32, background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white', fontSize: 28, cursor: 'pointer', zIndex: 2 }}
              aria-label="Close"
            >
              ×
            </button>
            <img src={artModalUrl} alt="Art File" style={{ maxHeight: '80vh', maxWidth: '90vw', background: '#222', borderRadius: 8, boxShadow: '0 0 24px #000' }} />
          </Box>
        </Modal>
      )}
    </MUIThemeProvider>
  );
};

export default FilmList;
